import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaCloud, FaLeaf, FaPaw, FaStethoscope, FaUtensils } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const products = [
  {
    icon: FaCloud,
    name: 'ECOSIS Web',
    description: 'ERP online para comércio, serviços e transporte, com emissão fiscal, financeiro, PDV e estoque no mesmo fluxo.',
    audience: 'Para empresas que precisam centralizar a operação',
    highlight: 'Gestão fiscal e comercial em nuvem',
    color: '#3B9ED4',
    path: '/ecosis-web',
  },
  {
    icon: FaLeaf,
    name: 'ECOSIS Agro',
    description: 'ERP em nuvem para produtores e operações do agronegócio, com apoio fiscal e controle do ciclo operacional.',
    audience: 'Para pequenas, médias e grandes propriedades',
    highlight: 'Rotina rural e fiscal no mesmo sistema',
    color: '#10A37F',
    path: '/ecosis-agro',
  },
  {
    icon: FaUtensils,
    name: 'ECOSIS Food',
    description: 'Gestão para restaurantes, bares e pizzarias com comandas, mesas, PDV, estoque e delivery.',
    audience: 'Para operação de salão, balcão e entrega',
    highlight: 'Atendimento mais rápido e operação mais enxuta',
    color: '#E85D8F',
    path: '/ecosis-food',
  },
  {
    icon: FaCalendarAlt,
    name: 'ECOSIS Agenda',
    description: 'Sistema em nuvem para agendamento online, comandas, caixa, financeiro, fiscal e relacionamento com o cliente.',
    audience: 'Para negócios orientados por agenda e recorrência',
    highlight: 'Agenda, caixa e fiscal integrados',
    color: '#6BBDDF',
    path: '/ecosis-agenda',
  },
  {
    icon: FaPaw,
    name: 'ECOSIS Pet',
    description: 'Gestão completa para pet shops e clínicas veterinárias com vacinas, prontuário e controle operacional.',
    audience: 'Para atendimento clínico e varejo pet',
    highlight: 'Prontuário e operação integrados',
    color: '#F3C130',
    path: '/ecosis-pet',
  },
  {
    icon: FaStethoscope,
    name: 'ECOSIS Clínicas',
    description: 'ERP em nuvem para clínicas médicas, odontológicas e fisioterapêuticas com agenda, prontuário, financeiro e NFSe.',
    audience: 'Para clínicas que precisam ganhar cadência',
    highlight: 'Atendimento, cobrança e gestão em uma base só',
    color: '#7DC4E8',
    path: '/ecosis-clinicas',
  },
];

const sectionVariant = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      staggerChildren: 0.08,
    },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

function ProductCard({ product }) {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = product.icon;
  const isClickable = Boolean(product.path);
  const content = (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '22px',
        }}
      >
        <div
          style={{
            width: '62px',
            height: '62px',
            borderRadius: '18px',
            background: isHovered
              ? `linear-gradient(135deg, ${product.color}26, ${product.color}42)`
              : `linear-gradient(135deg, ${product.color}20, ${product.color}35)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isHovered
              ? `0 16px 30px ${product.color}24`
              : `0 12px 28px ${product.color}18`,
            transform: isHovered ? 'translateY(-2px) scale(1.03)' : 'translateY(0) scale(1)',
            transition: 'all 0.24s ease',
          }}
        >
          <Icon size={28} color={product.color} />
        </div>

        <span
          style={{
            padding: '8px 12px',
            borderRadius: '9999px',
            background: isHovered ? `${product.color}14` : 'rgba(255,255,255,0.72)',
            border: `1px solid ${isHovered ? `${product.color}36` : 'rgba(59,158,212,0.12)'}`,
            color: isHovered ? product.color : product.path ? '#2178AB' : '#6A8899',
            fontWeight: 700,
            fontSize: '0.78rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            transition: 'all 0.24s ease',
          }}
        >
          {product.highlight}
        </span>
      </div>

      <h3
        style={{
          color: isHovered ? product.color : '#1A2D3A',
          fontSize: '1.3rem',
          fontWeight: 800,
          marginBottom: '10px',
          transition: 'color 0.24s ease',
        }}
      >
        {product.name}
      </h3>

      <p
        style={{
          color: '#2178AB',
          fontWeight: 600,
          fontSize: '0.92rem',
          marginBottom: '14px',
        }}
      >
        {product.audience}
      </p>

      <p
        style={{
          color: '#4A6680',
          lineHeight: 1.75,
          fontSize: '0.97rem',
          marginBottom: '24px',
        }}
      >
        {product.description}
      </p>

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          paddingTop: '18px',
          borderTop: '1px solid rgba(59,158,212,0.10)',
          color: isHovered ? product.color : '#4A6680',
          fontWeight: 700,
          transition: 'color 0.24s ease',
        }}
      >
        <span>{product.path ? 'Ver detalhes' : 'Disponível em breve'}</span>
        <span
          aria-hidden="true"
          style={{
            transform: isHovered && product.path ? 'translateX(3px)' : 'translateX(0)',
            transition: 'transform 0.24s ease',
          }}
        >
          {product.path ? '→' : '•'}
        </span>
      </div>
    </>
  );

  const cardStyles = {
    background: 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(235,246,252,0.92) 100%)',
    backdropFilter: 'blur(20px)',
    borderRadius: '26px',
    padding: '30px',
    minHeight: '100%',
    border: '1px solid rgba(59,158,212,0.12)',
    boxShadow: '0 10px 30px rgba(59,158,212,0.08)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    textDecoration: 'none',
  };

  return (
    <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
      <motion.article
        variants={cardVariant}
        whileHover={isClickable ? { y: -8, boxShadow: `0 20px 44px ${product.color}22` } : { y: -4 }}
        transition={{ duration: 0.24 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          ...cardStyles,
          border: `1px solid ${isHovered ? `${product.color}40` : 'rgba(59,158,212,0.12)'}`,
          boxShadow: isHovered
            ? `0 20px 44px ${product.color}1f`
            : '0 10px 30px rgba(59,158,212,0.08)',
          cursor: isClickable ? 'pointer' : 'default',
          transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
          transition: 'transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '-18%',
            right: '-6%',
            width: '160px',
            height: '160px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${product.color}16 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {product.path ? (
          <Link to={product.path} style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', color: 'inherit' }}>
            {content}
          </Link>
        ) : (
          content
        )}
      </motion.article>
    </div>
  );
}

function Products() {
  return (
    <section
      id="products"
      className="section-padding"
      style={{
        background: '#FAFEFF',
        marginTop: '-18px',
        paddingTop: '78px',
        position: 'relative',
        zIndex: 2,
      }}
    >
      <div className="container">
        <motion.div
          className="section-header text-center"
          variants={sectionVariant}
          initial="hidden"
          animate="visible"
        >
          <motion.p
            variants={cardVariant}
            style={{
              display: 'inline-flex',
              padding: '8px 14px',
              borderRadius: '9999px',
              background: 'rgba(59,158,212,0.08)',
              color: '#2178AB',
              fontWeight: 700,
              marginBottom: '16px',
            }}
          >
            Soluções por segmento
          </motion.p>
          <motion.h2 variants={cardVariant} className="section-title">
            Nossos Produtos
          </motion.h2>
          <motion.p variants={cardVariant} style={{ maxWidth: '720px', margin: '0 auto 34px', color: '#4A6680', lineHeight: 1.75 }}>
            Soluções completas e especializadas em nuvem para impulsionar a gestão do seu negócio, independente do seu segmento.
          </motion.p>
        </motion.div>

        <motion.div
          className="row"
          variants={sectionVariant}
          initial="hidden"
          animate="visible"
        >
          {products.map((product) => (
            <ProductCard key={product.name} product={product} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default Products;
